import { UserDtoTypeEnum } from '@coretava/api/core-service';
import { TotalPoints } from '@gamiphy/bl/reward';
import {
  UsersAggregates, UsersStatPerTier,
} from '@gamiphy/bl/user';
import { Http } from '@gamiphy/http';
import { Languages } from '@coretava/i18n';
import { UserWithMetadata } from './types';

export class UserApi {
  public static async listUsers(search?: string, limit?: number, skip?: number, sort?: string): Promise<ListUsersResponse> {
    const response = await Http.get<ListUsersResponse>({
      url: '/v2/users/',
      query: {
        search,
        limit,
        skip,
        sort,
        type: UserDtoTypeEnum.Member,
      },
    });
    return response.data;
  }

  public static async getUser(userId: string): Promise<UserWithMetadata> {
    const response = await Http.get<GetUserResponse>({
      url: `/v2/users/${userId}`,
    });

    return response.data.user;
  }

  public static async authorizeUser(token: string): Promise<UserWithMetadata> {
    const response = await Http.get<GetUserResponse>({
      url: '/v2/users/authorize',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data.user;
  }

  public static async register(user: RegisterUserRequestWithHash | RegisterUserRequestWithPassword): Promise<{
    user: UserWithMetadata,
    token: string,
    isNew: boolean
  }> {
    const {
      referrer,
      ...data
    } = user;

    const response = await Http.post<RegisterUserResponse>({
      url: '/v2/users/register',
      payload: {
        ...data,
        ...(referrer ? {
          referral: {
            referrer,
          },
        } : {}),
      },
    });

    return {
      ...response.data,
      isNew: response.status === 201,
    };
  }

  public static async registerWithToken(user: RegisterUserRequestWithHash | RegisterUserRequestWithPassword): Promise<{
    user: UserWithMetadata,
    token: string,
    isNew: boolean
  }> {
    const {
      referrer,
      ...data
    } = user;

    const response = await Http.post<RegisterUserResponse>({
      url: '/v2/users/validations/register',
      payload: {
        ...data,
        ...(referrer ? {
          referral: {
            referrer,
          },
        } : {}),
      },
    });

    return {
      ...response.data,
      isNew: response.status === 201,
    };
  }

  public static async upgrade(user: UpgradeUserRequest): Promise<{
    user: UserWithMetadata,
    token: string,
    isNew: boolean
  }> {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      hash,
      ...rest
    } = user;
    const response = await Http.put<RegisterUserResponse>({
      url: '/v2/users/upgrade',
      payload: rest,
    });

    return {
      ...response.data,
      isNew: response.status === 201,
    };
  }

  public static async exportToEmail(email: string): Promise<ListUsersResponse> {
    const response = await Http.post<ListUsersResponse>({
      url: '/v2/users/data/export',
      payload: { email },
    });

    return response.data;
  }

  public static async update(user: Partial<UserWithMetadata>): Promise<UserWithMetadata> {
    const response = await Http.patch<UpdateUser>({
      url: '/v2/users',
      payload: user,
    });

    return response.data.user;
  }

  public static async updatePoints(userId: string, data: UpdatePointsRequest): Promise<void> {
    await Http.put<GetUserResponse>({
      url: `/v2/users/${userId}/points`,
      payload: data,
    });
  }

  public static async connectUser(social: SocialConnectData): Promise<UserWithMetadata> {
    const response = await Http.put<GetUserResponse>({
      url: '/v2/users/social',
      payload: social,
    });
    return response.data.user;
  }

  public static async getTotalPoints(): Promise<TotalAppPoints> {
    const response = await Http.get<TotalPoints>({
      url: '/v2/users/points/total',
    });
    return response.data;
  }

  public static async getUsersAggregates(minPointGems?: number, maxPointGems?: number): Promise<UsersAggregates> {
    const response = await Http.get<UsersAggregates>({
      url: '/v2/users/users-aggregates',
      query: {
        minPointGems,
        maxPointGems,
      },
    });
    return response.data;
  }

  public static async getUsersStatsPerTier(): Promise<UsersStatPerTier[]> {
    const response = await Http.get<UsersStatPerTierResponse>({
      url: 'v2/users/stats/tier',
    });
    return response.data.tiers;
  }

  public static async getLevelReevaluationUsers(): Promise<number> {
    const response = await Http.get<LevelReevaluationUsers>({
      url: 'v2/users/stat/by-level-reevaluation',
    });
    return response.data.effectedMemebers;
  }

  public static async updatePhone(phone: string): Promise<UserWithMetadata> {
    const response = await Http.put<{
      user: UserWithMetadata
    }>({
      url: '/v2/users/validations/phone/assign',
      payload: { phone },
    });

    return response.data.user;
  }

  public static async verifyPhoneOtp(phone: string, code: string): Promise<VerifyPhoneResponse> {
    const response = await Http.put<VerifyPhoneResponse>({
      url: '/v2/users/validations/phone/verify',
      payload: { phone, code },
    });

    return response.data;
  }

  public static async resendPhoneOtp(phone: string): Promise<UserWithMetadata> {
    const response = await Http.put<{
      user: UserWithMetadata
    }>({
      url: '/v2/users/validations/phone/resend',
      payload: { phone },
    });

    return response.data.user;
  }
}

export type RegisterUserRequestWithHash = {
  firstName?: string,
  lastName?: string,
  avatar?: string,
  language?: Languages,
  country?: string,
  hash: string,
  type?: UserDtoTypeEnum,
  referrer?: string
} & ({ email: string } | { id: string })

export type RegisterUserRequestWithPassword = {
  id?: string,
  email?: string,
  firstName?: string,
  lastName?: string,
  avatar?: string,
  language?: Languages,
  type?: UserDtoTypeEnum,
  country?: string,
  password: string,
  referrer?: string
}

export type UpgradeUserRequest = {
  email: string,
  firstName?: string,
  lastName?: string,
  avatar?: string,
  language?: Languages,
  country?: string,
  hash: string,
  onboarding?: unknown,
}

export type UpdatePointsRequest = {
  points: {
    gems?: number,
    exp?: number,
  },
  op: 'inc' | 'dec',
  reason: string,
}

export type VerifyPhoneResponse = {
  user: UserWithMetadata,
  token: string
}

export type GetUserResponse = {
  user: UserWithMetadata
}

export type ListUsersResponse = {
  users: UserWithMetadata[] | [],
  count: number
}

export type RegisterUserResponse = {
  user: UserWithMetadata,
  token: string
}

export type WsUser = {
  id: string;
  data: UserWithMetadata;
}

export type UpdateUser = {
  user: UserWithMetadata
}

export type SocialConnectData = {
  code: string,
  redirectUri: string,
  type: string,
}

export type TotalAppPoints = {
  points: number,
  count: number,
}

export type UsersStatPerTierResponse = {
  tiers: UsersStatPerTier[],
}

export type LevelReevaluationUsers = {
  effectedMemebers: number,
}
